/**
 * Konflikt-Modal — zeigt eine Liste aller offenen Konflikte und erlaubt es dem
 * User pro Konflikt zu entscheiden, welche Version gewinnen soll.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  diffShiftFields,
  resolveKeepBoth,
  resolveKeepLocal,
  resolveKeepServer,
  useConflicts,
} from '../data/conflicts';
import { useLocations } from '../data/locations';
import type { OutboxEntry } from '../db/schema';
import { formatDate } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  onClose: () => void;
}

export function ConflictsModal({ onClose }: Props) {
  const conflicts = useConflicts() ?? [];

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Sync-Konflikte</h3>
            <p className="text-xs text-slate-400">
              Diese Schichten wurden lokal und parallel auf dem Server geändert.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 !min-h-0 px-2"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {conflicts.length === 0 ? (
          <p className="text-slate-400 text-sm">
            Aktuell keine Konflikte. Du kannst das Fenster schließen.
          </p>
        ) : (
          <ul className="space-y-4">
            {conflicts.map((c) => (
              <ConflictCard key={c.id} entry={c} onResolved={() => undefined} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConflictCard({
  entry,
  onResolved,
}: {
  entry: OutboxEntry;
  onResolved: () => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locations = useLocations() ?? [];
  const fields = diffShiftFields(entry);
  const conflict = entry.conflict;
  if (!conflict) return null;

  const locationName =
    locations.find((l) => l.id === conflict.serverVersion.locationId)?.name ?? '?';

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      qc.invalidateQueries();
      hapticSuccess();
      onResolved();
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="bg-slate-900/60 rounded-lg p-3 border border-amber-500/30">
      <div className="flex items-baseline justify-between mb-2">
        <p className="font-medium text-sm">
          {formatDate(conflict.serverVersion.shiftDate)} · {locationName}
        </p>
        <span className="text-[10px] uppercase tracking-wider text-amber-400">
          {entry.kind === 'shift.delete' ? 'Lösch-Konflikt' : 'Änderungs-Konflikt'}
        </span>
      </div>

      {/* Side-by-Side-Diff */}
      <div className="text-xs grid grid-cols-3 gap-2 bg-slate-950/40 rounded p-2 mb-3">
        <div className="text-slate-400 font-semibold">Feld</div>
        <div className="text-slate-400 font-semibold">Lokal</div>
        <div className="text-slate-400 font-semibold">Server</div>
        {fields.map((f) => (
          <FragmentRow key={f.label} field={f} />
        ))}
      </div>

      {error && <p className="text-sm text-red-400 mb-2">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => run(() => resolveKeepLocal(entry))}
          disabled={busy}
          className="text-xs bg-blue-600/80 hover:bg-blue-600 rounded px-3 py-2 disabled:opacity-50"
        >
          Lokal behalten
        </button>
        <button
          onClick={() => run(() => resolveKeepServer(entry))}
          disabled={busy}
          className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-3 py-2 disabled:opacity-50"
        >
          Server behalten
        </button>
        {entry.kind === 'shift.update' && (
          <button
            onClick={() => run(() => resolveKeepBoth(entry))}
            disabled={busy}
            className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 disabled:opacity-50"
          >
            Beide behalten (kopiert lokale als neue)
          </button>
        )}
      </div>
    </li>
  );
}

function FragmentRow({ field }: { field: ReturnType<typeof diffShiftFields>[number] }) {
  return (
    <>
      <div className="text-slate-300">{field.label}</div>
      <div
        className={`truncate ${field.changed ? 'text-blue-300 font-medium' : 'text-slate-400'}`}
      >
        {field.local}
      </div>
      <div
        className={`truncate ${field.changed ? 'text-emerald-300 font-medium' : 'text-slate-400'}`}
      >
        {field.server}
      </div>
    </>
  );
}
